import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, refreshSession } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const location = useLocation();

  // Attempt to refresh session if user appears to be authenticated but there's an auth error
  useEffect(() => {
    const attemptRefresh = async () => {
      if (!isAuthenticated && !isLoading && !isRefreshing) {
        // Check if there's a stored session that might need refreshing
        const storedSession = localStorage.getItem('auth_session');
        if (storedSession) {
          try {
            const sessionData = JSON.parse(storedSession);
            const sessionAge = Date.now() - sessionData.timestamp;
            
            // If session is less than 24 hours old, try to refresh
            if (sessionAge < 24 * 60 * 60 * 1000) {
              setIsRefreshing(true);
              await refreshSession();
            } else {
              // Remove expired session
              localStorage.removeItem('auth_session');
            }
          } catch (error) {
            console.error('Failed to refresh session:', error);
            localStorage.removeItem('auth_session');
          } finally {
            setIsRefreshing(false);
          }
        }
      }
    };

    attemptRefresh();
  }, [isAuthenticated, isLoading, refreshSession, isRefreshing]);

  if (isLoading || isRefreshing) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    // Redirect to login page with return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;